import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { settingsAPI } from '../utils/api';
import { renderMarkdown } from '../utils/markdown';

const Section = ({ n, title, children }) => (
  <section className="mb-6">
    <h2 className="text-xl sm:text-2xl font-bold mb-3 text-gray-900">
      {n}. {title}
    </h2>
    <div className="text-gray-700 leading-relaxed space-y-3 text-sm sm:text-base">
      {children}
    </div>
  </section>
);

const Sub = ({ n, children }) => (
  <p>
    <strong>{n}.</strong> {children}
  </p>
);

const Terms = () => {
  const [customHtml, setCustomHtml] = useState(null);
  useEffect(() => {
    settingsAPI.get()
      .then((r) => {
        const md = (r.data?.terms_markdown || '').trim();
        if (md) setCustomHtml(renderMarkdown(md));
      })
      .catch(() => {});
  }, []);
  return (
    <div className="min-h-screen">
      <div
        className="relative bg-cover bg-center py-20"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('https://customer-assets.emergentagent.com/job_elementor-site-1/artifacts/ff4gwxo8_ChatGPT%20Image%2031.%203.%202026%2023_45_22.png')`,
        }}
      >
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-32 sm:pt-44 lg:pt-52 pb-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3">
            Obchodní podmínky
          </h1>
          <p className="text-white/80 text-sm">
            platné a účinné od 1. ledna 2026
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <Card className="shadow-xl">
          <CardContent className="p-6 sm:p-10">
            {customHtml ? (
              <div
                className="prose prose-sm sm:prose-base max-w-none text-gray-800 [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:mb-4 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-6 [&_h2]:mb-3 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_strong]:font-semibold [&_p]:mb-3 [&_a]:text-orange-600 [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: customHtml }}
                data-testid="terms-custom-content"
              />
            ) : (
              <>
            <div className="text-center mb-8 pb-6 border-b">
              <p className="font-semibold text-lg">
                společnosti <strong>WildCamping s.r.o.</strong>
              </p>
              <p className="text-sm text-gray-600 mt-1">
                se sídlem na adrese uvedené na stránce Kontakt
              </p>
              <p className="text-sm text-gray-600">
                kontaktní e-mail: info@wildcamping.cz
              </p>
              <p className="text-sm text-gray-600 mt-2 italic">
                (dále jen „Provozovatel")
              </p>
            </div>

            <Section n="1" title="Úvodní ustanovení">
              <Sub n="1.1">
                Provozovatel provozuje na internetové adrese{' '}
                <strong>www.wildcamping.cz</strong> (dále jen „Webová stránka")
                online platformu (dále jen „Platforma"), prostřednictvím které
                vlastníci a oprávnění uživatelé pozemků (dále jen „Hostitelé")
                zveřejňují nabídku svých pozemků k dočasnému užívání pro účely
                kempování, a prostřednictvím které mohou další osoby (dále jen
                „Hosté") tyto pozemky rezervovat a uzavírat s Hostiteli smlouvu
                o krátkodobém pronájmu pozemku.
              </Sub>
              <Sub n="1.2">
                Hostitelé i Hosté jsou společně nazýváni „Uživatelé".
                Provozovatel sám není stranou žádné smlouvy uzavřené mezi
                Hostitelem a Hostem prostřednictvím Platformy – pouze poskytuje
                technické řešení pro takový obchodní vztah.
              </Sub>
              <Sub n="1.3">
                Tyto obchodní podmínky (dále jen „Podmínky") upravují v souladu
                s ustanovením § 1751 odst. 1 zákona č. 89/2012 Sb., občanský
                zákoník (dále jen „občanský zákoník") vzájemná práva a
                povinnosti mezi Provozovatelem a Uživateli vyplývající
                z používání Platformy a souvisejících služeb.
              </Sub>
              <Sub n="1.4">
                Podmínky jsou vyhotoveny v českém jazyce. Smluvní vztah
                založený registrací uživatelského účtu se uzavírá v českém
                jazyce.
              </Sub>
            </Section>

            <Section n="2" title="Definice pojmů">
              <Sub n="2.1">
                <strong>„Pozemek"</strong> je pozemek či jeho část ve vlastnictví
                nebo užívání Hostitele, který Hostitel nabízí k dočasnému
                užívání Hostům na základě smlouvy uzavřené prostřednictvím
                Platformy.
              </Sub>
              <Sub n="2.2">
                <strong>„Rezervace"</strong> je závazná dohoda mezi Hostem a
                Hostitelem o krátkodobém pronájmu Pozemku v konkrétním termínu,
                uzavřená prostřednictvím Platformy.
              </Sub>
              <Sub n="2.3">
                <strong>„Cena rezervace"</strong> je celková cena hrazená Hostem
                za pobyt, kterou stanovuje Hostitel ve své nabídce.
              </Sub>
              <Sub n="2.4">
                <strong>„Provize platformy"</strong> je odměna Provozovatele za
                poskytování služeb Platformy, sjednaná procentem z Ceny
                rezervace (čl. 7).
              </Sub>
              <Sub n="2.5">
                <strong>„Stripe"</strong> je platební brána provozovaná
                společností Stripe, Inc. (1 Grand Ave, South San Francisco, CA
                94080, USA), respektive jejími dceřinými společnostmi v EU,
                jejíž prostřednictvím probíhá veškerá úhrada plateb a výplata
                odměn Hostitelům.
              </Sub>
              <Sub n="2.6">
                <strong>„Organizovaná akce"</strong> je událost (např. dětský
                tábor, skupinová akce) pořádaná Hostitelem na konkrétním
                Pozemku v určeném termínu, která může být zveřejněna
                prostřednictvím Platformy. Hostitel je výhradně odpovědný za
                organizaci, průběh a bezpečnost takové akce.
              </Sub>
            </Section>

            <Section n="3" title="Uživatelský účet">
              <Sub n="3.1">
                Podmínkou plného využívání Platformy je registrace
                uživatelského účtu. Uživatel je povinen při registraci uvádět
                pravdivé a aktuální údaje. Změny v údajích je povinen
                bezodkladně aktualizovat ve svém účtu.
              </Sub>
              <Sub n="3.2">
                Přístup k účtu je zabezpečen e-mailem a heslem. Uživatel je
                povinen zachovávat mlčenlivost o přístupových údajích.
                Provozovatel neodpovídá za zneužití účtu způsobené
                neopatrností Uživatele.
              </Sub>
              <Sub n="3.3">
                Každý Uživatel může mít pouze jeden účet. Uživatel nesmí
                umožnit využívání svého účtu třetím osobám.
              </Sub>
              <Sub n="3.4">
                Provozovatel je oprávněn účet zablokovat nebo zrušit
                v případě, že Uživatel porušuje tyto Podmínky, právní předpisy
                nebo opakovaně poškozuje ostatní Uživatele. O blokaci či
                zrušení účtu bude Uživatel informován e-mailem s odůvodněním.
              </Sub>
              <Sub n="3.5">
                Uživatel je oprávněn svůj účet kdykoli zrušit. Zrušení účtu
                neovlivňuje již uzavřené Rezervace.
              </Sub>
            </Section>

            <Section n="4" title="Nabídka Pozemků a organizovaných akcí">
              <Sub n="4.1">
                Hostitel je povinen v nabídce Pozemku uvádět pravdivé,
                aktuální a kompletní informace včetně přesné polohy, fotografií,
                vybavení, ceny a pravidel pobytu.
              </Sub>
              <Sub n="4.2">
                Hostitel prohlašuje, že má veškerá oprávnění potřebná
                k pronájmu Pozemku (vlastnické nebo jiné právo, souhlas
                spoluvlastníků apod.) a že jeho nabídka neporušuje právní
                předpisy ani práva třetích osob (zejména ochranu přírody,
                stavební zákon, lesní zákon, daňové předpisy apod.).
              </Sub>
              <Sub n="4.3">
                Provozovatel neověřuje právní ani faktický stav Pozemku a
                neodpovídá za pravdivost informací zveřejněných Hostitelem.
              </Sub>
              <Sub n="4.4">
                Hostitel volí způsob potvrzování rezervací: <em>okamžitá
                rezervace</em> (smlouva vzniká okamžikem platby Hostem) nebo{' '}
                <em>rezervace s potvrzením</em> (smlouva vzniká až schválením
                Hostitelem v 24hodinovém limitu, jinak se automaticky ruší a
                platba vrací).
              </Sub>
              <Sub n="4.5">
                Hostitel může v rámci své nabídky vytvořit Organizovanou akci.
                Termín akce automaticky blokuje běžnou rezervaci pozemku.
                Provozovatel není pořadatelem akce ani neodpovídá za její
                obsah či průběh.
              </Sub>
            </Section>

            <Section n="5" title="Uzavření rezervace a smluvní vztah">
              <Sub n="5.1">
                Host vybere termín, počet osob a další parametry rezervace v
                rozhraní Platformy. Odesláním objednávky a úspěšným provedením
                platby přes Stripe Host přijímá nabídku Hostitele a uzavírá
                s ním smlouvu o krátkodobém pronájmu Pozemku (dále jen
                „Smlouva o pronájmu").
              </Sub>
              <Sub n="5.2">
                Smlouva o pronájmu je uzavřena přímo mezi Hostem a Hostitelem.
                Provozovatel není její stranou – pouze zprostředkovává
                technickou a platební infrastrukturu.
              </Sub>
              <Sub n="5.3">
                Potvrzení Rezervace, kontaktní údaje Hostitele a pokyny
                k příjezdu obdrží Host e-mailem ihned po úspěšném dokončení
                platby (resp. po schválení Hostitelem u rezervací s
                potvrzením).
              </Sub>
              <Sub n="5.4">
                Host je povinen na vyžádání Hostitele uvést identifikační
                údaje k rezervaci (např. ID rezervace, jméno, e-mail).
              </Sub>
            </Section>

            <Section n="6" title="Platby">
              <Sub n="6.1">
                Veškeré platby Hostů probíhají online prostřednictvím Stripe.
                Host hradí celkovou Cenu rezervace v okamžiku jejího uzavření.
              </Sub>
              <Sub n="6.2">
                Provozovatel automaticky zadrží svou Provizi platformy a
                zbylou částku převede Hostiteli na jeho připojený účet
                Stripe Connect podle vyplatních lhůt Stripe (zpravidla do 7
                pracovních dnů od dokončení pobytu).
              </Sub>
              <Sub n="6.3">
                Hostitel je povinen mít aktivní účet Stripe Connect a poskytnout
                potřebné identifikační údaje (KYC) podle požadavků Stripe a
                platných předpisů proti praní špinavých peněz. Bez aktivního
                Stripe účtu nelze přijímat platby a nabídku zveřejnit.
              </Sub>
              <Sub n="6.4">
                Hostitel sám odpovídá za zdanění svých příjmů a za případné
                další povinnosti (DPH, místní poplatky z pobytu, evidenční
                kniha ubytovaných apod.).
              </Sub>
            </Section>

            <Section n="7" title="Provize platformy">
              <Sub n="7.1">
                Standardní Provize platformy činí <strong>15&nbsp;%</strong> z
                Ceny rezervace; Hostiteli je tedy vyplaceno <strong>85&nbsp;%</strong>{' '}
                z Ceny rezervace. Provize je odečtena automaticky v okamžiku
                platby (princip „destination charge" v Stripe Connect).
              </Sub>
              <Sub n="7.2">
                Provize zahrnuje provoz Platformy, marketing, zákaznickou
                podporu, e-mailové notifikace, automatizované rezervační
                procesy a další technické služby. Provize neobsahuje DPH;
                Hostitel obdrží daňový doklad ve výši své čistě vyplacené
                částky.
              </Sub>
              <Sub n="7.3">
                Provozovatel si vyhrazuje právo Provizi platformy upravit;
                jakákoli změna bude oznámena minimálně 30 dnů předem e-mailem
                všem registrovaným Hostitelům a uveřejněna na Webové stránce.
              </Sub>
            </Section>

            <Section n="8" title="Storno podmínky a vrácení peněz">
              <Sub n="8.1">
                Host je oprávněn Rezervaci zrušit a požádat o vrácení peněz za
                níže uvedených podmínek:
              </Sub>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>
                  <strong>Více než 7 dní</strong> před začátkem pobytu –{' '}
                  <strong>100 % vrácení</strong> Ceny rezervace.
                </li>
                <li>
                  <strong>3–7 dní</strong> před začátkem pobytu –{' '}
                  <strong>50 % vrácení</strong> Ceny rezervace.
                </li>
                <li>
                  <strong>Méně než 3 dny</strong> nebo po začátku pobytu –{' '}
                  <strong>bez nároku na vrácení</strong>.
                </li>
              </ul>
              <Sub n="8.2">
                V případě, kdy zruší Rezervaci Hostitel, je Hostovi vrácena{' '}
                <strong>celá Cena rezervace včetně Provize platformy</strong>.
                Opakované zrušení rezervací ze strany Hostitele může vést k
                pozastavení nebo zrušení účtu.
              </Sub>
              <Sub n="8.3">
                Vrácení peněz je provedeno přes Stripe na stejnou platební
                metodu, kterou Host použil k platbě, obvykle do 5–10
                pracovních dnů.
              </Sub>
              <Sub n="8.4">
                V případě vyšší moci (extrémní počasí, přírodní katastrofa,
                úřední zákaz apod.) je možné individuální posouzení; v takovém
                případě kontaktujte podporu na{' '}
                <a href="mailto:info@wildcamping.cz" className="text-orange-600 hover:underline">
                  info@wildcamping.cz
                </a>
                .
              </Sub>
            </Section>

            <Section n="9" title="Povinnosti Hostů">
              <Sub n="9.1">
                Host je povinen chovat se na Pozemku ohleduplně, dodržovat
                pravidla stanovená Hostitelem, respektovat sousedy, hospodářská
                zvířata a přírodu.
              </Sub>
              <Sub n="9.2">
                Host odpovídá za škody způsobené sobě, svým doprovodným
                osobám, zvířatům nebo majetku Hostitele.
              </Sub>
              <Sub n="9.3">
                Host je povinen po skončení pobytu Pozemek opustit
                v původním stavu (pořádek, žádný odpad, uhašený oheň).
              </Sub>
              <Sub n="9.4">
                Host nesmí překročit maximální počet osob uvedený v nabídce
                ani převést rezervaci na třetí osobu bez souhlasu Hostitele.
              </Sub>
            </Section>

            <Section n="10" title="Povinnosti Hostitelů">
              <Sub n="10.1">
                Hostitel se zavazuje umožnit Hostu pokojné a nerušené užívání
                Pozemku v dohodnutém termínu a v rozsahu podle své nabídky.
              </Sub>
              <Sub n="10.2">
                Hostitel je povinen zajistit, aby Pozemek odpovídal popisu
                v nabídce. V opačném případě má Host právo na přiměřenou slevu
                z Ceny rezervace nebo na vrácení peněz.
              </Sub>
              <Sub n="10.3">
                Hostitel je povinen poskytnout Hostovi přesné a srozumitelné
                pokyny k příjezdu a kontakt pro případ problémů během pobytu.
              </Sub>
              <Sub n="10.4">
                Hostitel je výhradně odpovědný za bezpečnost pozemku, soulad
                s předpisy o ochraně přírody, požární bezpečnosti, hygieně,
                a za splnění oznamovacích povinností vůči obecním úřadům či
                ubytovacím poplatkům.
              </Sub>
            </Section>

            <Section n="11" title="Hodnocení a recenze">
              <Sub n="11.1">
                Po skončení pobytu může Host hodnotit pozemek a Hostitel může
                hodnotit Hosta. Hodnocení obou stran se zveřejňuje současně
                po vyplnění oběma stranami nebo automaticky po 7 dnech.
              </Sub>
              <Sub n="11.2">
                Hodnocení musí být věcné, pravdivé a slušné. Provozovatel je
                oprávněn odstranit recenze obsahující nadávky, urážky, lživé
                informace nebo osobní údaje třetích osob.
              </Sub>
            </Section>

            <Section n="12" title="Odpovědnost Provozovatele">
              <Sub n="12.1">
                Provozovatel poskytuje Platformu „tak, jak je" („as is"). Není
                zaručena nepřetržitá dostupnost; v případě technických prací,
                výpadku internetu, sítě nebo služeb třetích stran (Stripe,
                Resend, Mongo Atlas apod.) může dojít k dočasné nedostupnosti
                Platformy.
              </Sub>
              <Sub n="12.2">
                Provozovatel <strong>není stranou</strong> Smlouvy o pronájmu a
                neodpovídá za:
              </Sub>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>obsah, pravdivost a úplnost nabídek Hostitelů;</li>
                <li>fyzický stav, bezpečnost a oprávnění k užívání Pozemků;</li>
                <li>průběh Organizovaných akcí pořádaných Hostiteli;</li>
                <li>chování Hostů či Hostitelů během pobytu;</li>
                <li>případnou škodu na zdraví nebo majetku.</li>
              </ul>
              <Sub n="12.3">
                V případě sporu mezi Hostem a Hostitelem se obě strany pokusí
                nejprve dohodnout vzájemně. Provozovatel může na vyžádání
                obou stran poskytnout informace dostupné ze své Platformy.
              </Sub>
            </Section>

            <Section n="13" title="Ochrana osobních údajů">
              <Sub n="13.1">
                Zpracování osobních údajů Uživatelů probíhá v souladu s
                Nařízením GDPR (EU 2016/679) a zákonem č. 110/2019 Sb., o
                zpracování osobních údajů. Bližší informace o rozsahu, účelu,
                době zpracování a právech subjektu údajů jsou uvedeny v
                samostatném dokumentu „Zásady zpracování osobních údajů".
              </Sub>
              <Sub n="13.2">
                Uživatel souhlasí se zasíláním transakčních e-mailových
                zpráv (potvrzení rezervace, připomenutí, hodnocení). Souhlas s
                marketingovými e-maily je dobrovolný a lze jej kdykoliv
                odvolat.
              </Sub>
            </Section>

            <Section n="14" title="Licenční ujednání">
              <Sub n="14.1">
                Uložením fotografií, popisů a dalšího obsahu na Platformu
                Uživatel uděluje Provozovateli bezúplatnou nevýhradní licenci
                k jejich užití pro účely provozu Platformy a propagace
                Platformy (zejména na sociálních sítích a v reklamě), bez
                časového či územního omezení.
              </Sub>
              <Sub n="14.2">
                Uživatel prohlašuje, že má potřebná autorská práva
                k zveřejňovanému obsahu a že tento obsah neporušuje práva
                třetích osob.
              </Sub>
            </Section>

            <Section n="15" title="Spotřebitelská ustanovení">
              <Sub n="15.1">
                Pro vztahy mezi Provozovatelem a Uživatelem, který je
                spotřebitelem ve smyslu § 419 občanského zákoníku, se použijí
                ustanovení o spotřebitelských smlouvách (§ 1810 a násl.
                občanského zákoníku) a zákona č. 634/1992 Sb., o ochraně
                spotřebitele.
              </Sub>
              <Sub n="15.2">
                Spotřebitel má právo odstoupit od smlouvy o uživatelském účtu
                do 14 dnů od její uzavření bez udání důvodu.
              </Sub>
              <Sub n="15.3">
                Mimosoudní řešení spotřebitelských sporů zajišťuje{' '}
                <strong>Česká obchodní inspekce</strong> (
                <a href="https://adr.coi.cz/cs" target="_blank" rel="noreferrer" className="text-orange-600 hover:underline">
                  adr.coi.cz
                </a>
                ), případně platforma EU pro online řešení sporů (ODR):{' '}
                <a href="http://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer" className="text-orange-600 hover:underline">
                  ec.europa.eu/consumers/odr
                </a>
                .
              </Sub>
            </Section>

            <Section n="16" title="Závěrečná ustanovení">
              <Sub n="16.1">
                Tyto Podmínky se řídí právem České republiky, zejména
                občanským zákoníkem. Veškeré spory budou řešeny příslušnými
                soudy České republiky.
              </Sub>
              <Sub n="16.2">
                Provozovatel je oprávněn Podmínky přiměřeně měnit. O změně
                bude Uživatel informován minimálně 15 dnů předem e-mailem na
                adresu uvedenou v jeho účtu. Pokud Uživatel se změnou
                nesouhlasí, je oprávněn účet zrušit; v opačném případě se má
                za to, že se změnou souhlasí.
              </Sub>
              <Sub n="16.3">
                Pokud se některé ustanovení Podmínek stane neplatným nebo
                neúčinným, není tím dotčena platnost ostatních ustanovení.
                Neplatné ustanovení se nahradí ustanovením, jehož smysl mu
                nejvíce odpovídá.
              </Sub>
              <Sub n="16.4">
                Kontakt na Provozovatele:{' '}
                <a href="mailto:info@wildcamping.cz" className="text-orange-600 hover:underline">
                  info@wildcamping.cz
                </a>
                .
              </Sub>
            </Section>

            <div className="mt-10 pt-6 border-t text-center text-sm text-gray-500">
              <p>WildCamping s.r.o.</p>
              <p>Tato verze obchodních podmínek je platná a účinná od 1. ledna 2026.</p>
            </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default Terms;
